/**
 * Grammar Info Tips for Verb & Pronoun categories in Kabyle
 * Explains the aspectual system of Kabyle (Accompli / Inaccompli / Aoriste modal avec Ad / Impératif)
 */

export interface GrammarTip {
  title: string;
  badge: string;
  badgeColor: string; // Tailwind color classes
  borderColor: string;
  summary: string;
  rules: { label: string; explanation: string; example: string }[];
  didYouKnow?: string;
}

export const CATEGORY_GRAMMAR_TIPS: Record<string, GrammarTip> = {
  'Verbes au Passé': {
    title: "Le Passé en Kabyle : L'Aspect Accompli (Izri)",
    badge: 'Izri • Prétérit',
    badgeColor: 'bg-amber-800 text-amber-100 border-amber-900',
    borderColor: 'border-amber-300 bg-amber-50/70',
    summary:
      "En kabyle, le passé correspond à l'aspect accompli (Izri) : l'action est achevée au moment où l'on parle. La conjugaison se fait par l'ajout d'affixes de personne (préfixes/suffixes) directement sur la base verbale modifiée.",
    rules: [
      {
        label: 'Personnes & Affixes types',
        explanation: 'Nekk: -eɣ | Kečč/Kemm: t-...-eḍ | Netta: y-...-a/ø | Nettat: t-...-a/ø | Nekni: n-...-a/ø | Kunwi: t-...-em | Nutni: ...-en | Nutenti: ...-ent',
        example: 'Ččiɣ (J\'ai mangé) • Teččiḍ (Tu as mangé) • Yečča (Il a mangé) • Nečča (Nous/On a mangé) • Ččan (Ils ont mangé)'
      },
      {
        label: 'Verbes d\'état au présent',
        explanation: 'Pour les verbes de sensation ou d\'état, le prétérit traduit un état actuel (présent en français) car le sentiment est déjà acquis.',
        example: 'Ẓriɣ (Je sais) • Bɣiɣ (Je veux) • Fhemeɣ (Je comprends) • Ḥemmleɣ (J\'aime)'
      }
    ],
    didYouKnow:
      "En kabyle, « On » (pronom indéfini) se traduit naturellement par la 1ère personne du pluriel (Nekni / Nukni : Nečča = « On a mangé » ou « Nous avons mangé »)."
  },

  'Verbes au Futur & Présent': {
    title: "L'Avenir, l'Intention & le Présent Continu : Ad + Urmir & Urmir Ussid",
    badge: 'Ad + Urmir & Urmir Ussid',
    badgeColor: 'bg-emerald-800 text-emerald-100 border-emerald-900',
    borderColor: 'border-emerald-300 bg-emerald-50/70',
    summary:
      "Le kabyle ne possède pas de temps « Présent » ou « Futur » autonome comme en français. Il combine la particule modale « Ad » avec l'aoriste (Urmir) pour le futur/l'intention, et l'aoriste intensif (Urmir ussid) pour l'action continue en cours.",
    rules: [
      {
        label: 'Futur & Intention (Ad + Urmir)',
        explanation: 'La particule préverbale « Ad » (éventualité, projet, souhait) + les préfixes de personne (eɣ, t-...-eḍ, y-, t-, n-, t-...-em, ...-en).',
        example: 'Ad ččeɣ (Je mangerai / Je vais manger) • Ad yečč (Il mangera) • Ad nečč (Nous mangerons / On mangera) • Ad ččen (Ils mangeront)'
      },
      {
        label: 'Présent continu / Habitude (Urmir ussid)',
        explanation: 'Marqué par le préfixe duratif « Tt- » ou le redoublement de consonne pour exprimer l\'action en train de se faire ou habituelle.',
        example: 'Ttwaliɣ (Je vois / Je regarde actuellement) • Tettwaliḍ (Tu vois) • Yettwali (Il voit) • Ttwalin (Ils voient)'
      }
    ],
    didYouKnow:
      "La particule « Ad » se transforme phonétiquement en « A » ou « As » selon les pronoms affixes qui s'intercalent (ex: Ad as-t-fkeɣ = Je vais le lui donner)."
  },

  'Verbes': {
    title: "Les Verbes : L'Impératif & Thème Verbal Simple (Anaḍ)",
    badge: 'Anaḍ • Impératif & Actions',
    badgeColor: 'bg-sky-800 text-sky-100 border-sky-900',
    borderColor: 'border-sky-300 bg-sky-50/70',
    summary:
      "Les verbes au singulier correspondent généralement à la forme nue du verbe (l'impératif 2e pers. singulier), qui sert aussi de racine fondamentale en lexicographie berbère.",
    rules: [
      {
        label: 'Impératif direct',
        explanation: 'Donne un ordre ou une consigne directe sans affixe au singulier masculin/féminin.',
        example: 'Ečč (Mange !) • Sew (Bois !) • Ruḥ (Pars !) • Qqim (Assieds-toi !) • Kker (Lève-toi !)'
      },
      {
        label: 'Particule d\'orientation « -d » (rapprochement)',
        explanation: 'Le suffixe « -d » indique une action dirigée vers le locuteur (vers ici/vers moi).',
        example: 'Awi (Emporte) vs Awi-d (Apporte ici) • Ewweḍ (Arriver là-bas) vs Ewweḍ-d (Arriver ici)'
      }
    ]
  },
  "Verbes d'Action": {
    title: "Les Verbes : L'Impératif & Thème Verbal Simple (Anaḍ)",
    badge: 'Anaḍ • Impératif & Actions',
    badgeColor: 'bg-sky-800 text-sky-100 border-sky-900',
    borderColor: 'border-sky-300 bg-sky-50/70',
    summary:
      "Les verbes au singulier correspondent généralement à la forme nue du verbe (l'impératif 2e pers. singulier), qui sert aussi de racine fondamentale en lexicographie berbère.",
    rules: [
      {
        label: 'Impératif direct',
        explanation: 'Donne un ordre ou une consigne directe sans affixe au singulier masculin/féminin.',
        example: 'Ečč (Mange !) • Sew (Bois !) • Ruḥ (Pars !) • Qqim (Assieds-toi !) • Kker (Lève-toi !)'
      },
      {
        label: 'Particule d\'orientation « -d » (rapprochement)',
        explanation: 'Le suffixe « -d » indique une action dirigée vers le locuteur (vers ici/vers moi).',
        example: 'Awi (Emporte) vs Awi-d (Apporte ici) • Ewweḍ (Arriver là-bas) vs Ewweḍ-d (Arriver ici)'
      }
    ]
  },

  'Pensée & Communication': {
    title: "Verbes de Perception & Verbes d'État (Tamusni d Wawal)",
    badge: 'Tamusni • Savoir & Sentiments',
    badgeColor: 'bg-purple-800 text-purple-100 border-purple-900',
    borderColor: 'border-purple-300 bg-purple-50/70',
    summary:
      "Les verbes cognitifs, de perception (entendre, voir) et de sentiment (vouloir, savoir) utilisent le thème accompli (Izri) pour exprimer un état d'esprit présent et certain.",
    rules: [
      {
        label: 'Conjugaison d\'état au présent',
        explanation: 'Pour exprimer ce que l\'on pense, sait ou veut en ce moment même, on conjugue au prétérit.',
        example: 'Ẓriɣ (Je sais) • Teẓriḍ (Tu sais) • Yeẓra (Il sait) • Neẓra (On sait / Nous savons) • Ẓran (Ils savent)'
      },
      {
        label: 'Vouloir (Bɣu)',
        explanation: 'En kabyle, le sentiment présent s\'exprime avec le thème accompli du verbe Bɣu.',
        example: 'Bɣiɣ (Je veux) • Tebɣiḍ (Tu veux) • Yebɣa (Il veut) • Nebɣa (Nous voulons) • Bɣan (Ils veulent)'
      }
    ]
  },

  'Double Pronominalisation': {
    title: "Règle d'or de la Pronominalisation : Datif (COI) avant Accusatif (COD)",
    badge: 'Affixes COI + COD',
    badgeColor: 'bg-rose-800 text-rose-100 border-rose-900',
    borderColor: 'border-rose-300 bg-rose-50/70',
    summary:
      "Quand deux pronoms compléments sont rattachés au verbe en kabyle, le COI (à qui ? Datif) se place TOUJOURS AVANT le COD (quoi ? Accusatif).",
    rules: [
      {
        label: 'Ordre absolu : Verbe + COI + COD',
        explanation: 'Affixe COI (-iyi, -ak, -am, -as, -aɣ, -awen, -asen) + Affixe COD (-t [le], -tt [la], -ten [les]).',
        example: 'Efk-iyi-t (Donne-le-moi) • Efk-as-t (Donne-le-lui) • Fkiɣ-as-t (Je le lui ai donné)'
      },
      {
        label: 'Antéposition avec « Ad » ou la négation « Ur »',
        explanation: 'Devant « Ad » (futur) ou « Ur ... ara » (négation), les deux pronoms passent AVANT le verbe, toujours dans le même ordre (COI + COD).',
        example: 'Ad as-t-fkeɣ (Je vais le lui donner) • Ur as-t-fkiɣ ara (Je ne le lui ai pas donné)'
      }
    ]
  },

  'Mots de liaison': {
    title: "Mots de Liaison, Conjonctions & Prépositions (Tusnawin n Tuqqna)",
    badge: 'Connecteurs & Nuances',
    badgeColor: 'bg-indigo-800 text-indigo-100 border-indigo-900',
    borderColor: 'border-indigo-300 bg-indigo-50/70',
    summary:
      "Les mots de liaison relient les propositions et précisent les relations de temps, de cause, de condition ou de lieu. En kabyle, plusieurs termes français aux traductions proches ou identiques (comme « Quand », « Parce que », « Si », « Mais ») correspondent à des nuances précises qu'il est essentiel de distinguer.",
    rules: [
      {
        label: '1. Le Temps : « Quand » Général (Mi) vs Passé Révolu (Asmi)',
        explanation: '« Mi » s\'utilise pour le temps en général, le présent ou le futur (ex: Mi ara...). « Asmi » s\'emploie spécifiquement pour des souvenirs et faits passés révolus (« À l\'époque où »).',
        example: 'Mi tebɣiḍ (Quand tu veux) vs Asmi lliɣ meẓẓiyeɣ (Quand j\'étais petit) • Segmi (Depuis que) • Skud (Tant que)'
      },
      {
        label: '2. La Cause & l\'Effet : « Parce que » (Acku / Axaṭer) vs « Puisque » (Imi)',
        explanation: '« Acku » et « Axaṭer » introduisent la cause directe. « Imi » traduit une cause évidente ou déjà connue. « Ihi / Dɣa » marquent la conclusion logique (Donc / Alors).',
        example: 'Axaṭer / Acku lluẓeɣ (Parce que j\'ai faim) • Imi teẓriḍ (Puisque tu sais) • Ihi (Donc) • Daymi / Ɣef waya (C\'est pourquoi)'
      },
      {
        label: '3. La Condition : « Si » possible (Ma) vs « Si seulement / Regret » (Lemmer)',
        explanation: '« Ma » introduit une condition réalisable au présent ou au futur. « Lemmer » exprime une hypothèse irréalisable, un regret ou l\'irréel du passé.',
        example: 'Ma yella tebɣiḍ (Si tu veux) vs Lemmer ẓriɣ (Si seulement j\'avais su !) • Send / Qbel (Avant que) • Almi (Jusqu\'à ce que)'
      },
      {
        label: '4. L\'Opposition & Concession : « Mais » (Maca / Lameɛna) vs « Même si » (Xas / Ulamma)',
        explanation: '« Maca » et « Lameɛna » marquent la restriction (Mais/Cependant). « Xas », « Ɣas akken » et « Ulamma » expriment la concession (Même si / Bien que). « Wanag / Wama » traduit Sinon / Faute de quoi.',
        example: 'Bɣiɣ maca ur zmireɣ ara (Je veux mais je ne peux pas) • Xas akken teɛyeḍ (Bien que tu sois fatigué)'
      },
      {
        label: '5. But & Comparaison : « Pour que » (Iwakken / Akken) & « Comme » (Am / Amzun)',
        explanation: '« Iwakken » (ou Akken) + Ad exprime le but/l\'intention (Pour que / Afin de). « Am » compare directement (Comme), tandis que « Amzun » ou « Am akken » traduit « Comme si / On dirait que ».',
        example: 'Iwakken ad teččeḍ (Pour que tu manges) • Am gma-k (Comme ton frère) • Amzun d targit (Comme si c\'était un rêve)'
      },
      {
        label: '6. Prépositions Spatiales Clés : Deg (Dans), Ɣer (Vers), Seg (De/Depuis)',
        explanation: '« Deg » (Dans/En), « Ɣer » (Vers/Chez/Direction), « Seg » (De/Origine), « Ɣef » (Sur/À propos de), « Gar » (Entre), « Mebla » (Sans).',
        example: 'Deg uxxam (Dans la maison) • Ɣer ssuq (Vers le marché) • Seg taddart (Depuis le village) • Gar-aneɣ (Entre nous)'
      }
    ],
    didYouKnow:
      "La particule « D » signifie à la fois « Et » et « Avec » pour relier deux noms (ex: « Nekk d kečč » = Toi et moi / Moi avec toi), tout en servant aussi de copule d'identification (« D nekk » = C'est moi) !"
  }
};
